/**
 * Notification Service
 * API calls for notifications
 */
import api from './api';
import {
  Notification,
  NotificationListResponse,
  UnreadCountResponse,
  NotificationCreate,
  NotificationType,
  NotificationPriority
} from '../types/notification';

export const notificationService = {
  /**
   * Get list of notifications for current user
   */
  async getNotifications(params?: {
    skip?: number;
    limit?: number;
    unread_only?: boolean;
    notification_type?: NotificationType;
    priority?: NotificationPriority;
  }): Promise<NotificationListResponse> {
    const response = await api.get('/notifications/', { params });
    return response.data;
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<UnreadCountResponse> {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },

  /**
   * Get a specific notification by ID
   */
  async getNotification(id: number): Promise<Notification> {
    const response = await api.get(`/notifications/${id}`);
    return response.data;
  },

  /**
   * Create a notification (HR/Admin only)
   */
  async createNotification(data: NotificationCreate): Promise<Notification> {
    const response = await api.post('/notifications/', data);
    return response.data;
  },

  /**
   * Create bulk notifications (HR/Admin only)
   */
  async createBulkNotifications(
    user_ids: number[],
    notification_type: NotificationType,
    title: string,
    message: string,
    link?: string,
    priority?: NotificationPriority,
    expires_at?: string
  ): Promise<{ message: string; user_count: number }> {
    const response = await api.post('/notifications/bulk', {
      user_ids,
      notification_type,
      title,
      message,
      link,
      priority,
      expires_at
    });
    return response.data;
  },

  /**
   * Mark a notification as read
   */
  async markAsRead(id: number): Promise<Notification> {
    const response = await api.put(`/notifications/${id}/mark-read`);
    return response.data;
  },

  /**
   * Mark a notification as unread
   */
  async markAsUnread(id: number): Promise<Notification> {
    const response = await api.put(`/notifications/${id}/mark-unread`);
    return response.data;
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<{ message: string }> {
    const response = await api.put('/notifications/mark-all-read');
    return response.data;
  },

  /**
   * Delete a notification
   */
  async deleteNotification(id: number): Promise<void> {
    await api.delete(`/notifications/${id}`);
  },

  /**
   * Delete all read notifications
   */
  async deleteAllRead(): Promise<{ message: string }> {
    const response = await api.delete('/notifications/');
    return response.data;
  }
};
